import './styles.css';

const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_PIXELS = 40 * 1000 * 1000;
const WORKER_START_TIMEOUT_MS = 30000;
const OCR_ASSETS = {
  workerPath: '/assets/ocr/worker.min.js',
  corePath: '/assets/ocr/core',
  langPath: '/assets/ocr/lang/4.0.0',
};

const elements = {
  fileInput: document.querySelector('#file-input'),
  dropZone: document.querySelector('#drop-zone'),
  previewPanel: document.querySelector('#image-preview-panel'),
  preview: document.querySelector('#image-preview'),
  imageName: document.querySelector('#image-name'),
  imageMeta: document.querySelector('#image-meta'),
  removeImage: document.querySelector('#remove-image'),
  language: document.querySelector('#language-select'),
  recognize: document.querySelector('#recognize-button'),
  clear: document.querySelector('#clear-button'),
  status: document.querySelector('#status-message'),
  progress: document.querySelector('#progress-indicator'),
  resultPanel: document.querySelector('#result-panel'),
  result: document.querySelector('#result-text'),
  resultMeta: document.querySelector('#result-meta'),
  copy: document.querySelector('#copy-button'),
  download: document.querySelector('#download-button'),
  retry: document.querySelector('#retry-button'),
};

const state = {
  file: null,
  mime: '',
  width: 0,
  height: 0,
  previewUrl: null,
  language: 'eng',
  resultText: '',
  status: '',
  statusKind: '',
  working: false,
  runId: 0,
  worker: null,
};

// Tesseract.js 7 leaves its createWorker promise pending when a language
// request fails. Track the browser Worker during startup so a failed request
// can settle the UI and release the orphan worker before a retry.
const workerTracker = {
  nativeWorker: null,
  wrappedWorker: null,
  workers: new Set(),
};

function installWorkerTracker() {
  if (workerTracker.wrappedWorker || typeof globalThis.Worker !== 'function') return;
  const NativeWorker = globalThis.Worker;
  const tracked = workerTracker.workers;
  class TrackedWorker extends NativeWorker {
    constructor(...args) {
      super(...args);
      tracked.add(this);
    }

    terminate() {
      tracked.delete(this);
      return super.terminate();
    }
  }
  workerTracker.nativeWorker = NativeWorker;
  workerTracker.wrappedWorker = TrackedWorker;
  globalThis.Worker = TrackedWorker;
}

function terminateTrackedWorkers() {
  for (const worker of workerTracker.workers) {
    try {
      worker.terminate();
    } catch {
      // The browser may already have disposed a worker after navigation.
    }
  }
  workerTracker.workers.clear();
}

function ascii(bytes, start, length) {
  return String.fromCharCode(...bytes.slice(start, start + length));
}

function readU32BE(bytes, offset) {
  return ((bytes[offset] * 0x1000000) + (bytes[offset + 1] * 0x10000) + (bytes[offset + 2] * 0x100) + bytes[offset + 3]) >>> 0;
}

function readU32LE(bytes, offset) {
  return (bytes[offset] + (bytes[offset + 1] * 0x100) + (bytes[offset + 2] * 0x10000) + (bytes[offset + 3] * 0x1000000)) >>> 0;
}

function readU24(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16);
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function setStatus(message, kind = '') {
  state.status = message;
  state.statusKind = kind;
  elements.status.textContent = message;
  elements.status.className = `status-message${kind ? ` ${kind}` : ''}`;
  elements.progress.hidden = !state.working;
}

function revokePreview() {
  if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
  state.previewUrl = null;
}

function stopWorker() {
  const worker = state.worker;
  state.worker = null;
  if (worker) worker.terminate().catch(() => {});
  terminateTrackedWorkers();
}

function cancelCurrentRun() {
  state.runId += 1;
  state.working = false;
  stopWorker();
}

function clearSelectedImage() {
  revokePreview();
  state.file = null;
  state.mime = '';
  state.width = 0;
  state.height = 0;
  state.resultText = '';
}

function render() {
  const hasFile = Boolean(state.file);
  elements.previewPanel.hidden = !hasFile;
  if (hasFile) {
    elements.preview.src = state.previewUrl || '/favicon.svg';
    elements.preview.alt = `Preview of ${state.file.name}`;
    elements.imageName.textContent = state.file.name;
    elements.imageMeta.textContent = `${formatBytes(state.file.size)} · ${state.width} × ${state.height} · ${state.mime.replace('image/', '').toUpperCase()}`;
  } else {
    elements.preview.src = '/favicon.svg';
    elements.preview.alt = 'Selected image preview';
    elements.imageName.textContent = '';
    elements.imageMeta.textContent = '';
  }

  elements.fileInput.disabled = state.working;
  elements.language.disabled = state.working;
  elements.recognize.disabled = !hasFile || state.working;
  elements.clear.disabled = (!hasFile && !state.resultText && !state.working);
  elements.removeImage.disabled = state.working;
  elements.retry.disabled = !hasFile || state.working;
  const showResultWorkspace = Boolean(state.resultText)
    || (hasFile && (state.working || state.statusKind === 'error'));
  elements.resultPanel.hidden = !showResultWorkspace;
  if (elements.result.value !== state.resultText) elements.result.value = state.resultText;
  const characters = state.resultText.length;
  elements.resultMeta.textContent = state.resultText ? `${characters.toLocaleString()} characters` : '';
  elements.progress.hidden = !state.working;
}

function isPng(bytes) {
  return bytes.length >= 8 && ascii(bytes, 0, 8) === '\x89PNG\r\n\x1a\n';
}

function isJpeg(bytes) {
  return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
}

function findPngChunk(bytes, wanted) {
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const size = readU32BE(bytes, offset);
    const type = ascii(bytes, offset + 4, 4);
    if (size > bytes.length - offset - 12) return false;
    if (type === wanted) return true;
    offset += 12 + size;
    if (offset % 2) offset += 0;
    if (type === 'IEND') break;
  }
  return false;
}

function validateWebp(bytes) {
  if (bytes.length < 20 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') {
    throw new Error('The file is not a WebP image. The file contents were checked.');
  }
  const riffSize = readU32LE(bytes, 4);
  const containerEnd = 8 + riffSize;
  if (riffSize < 4 || containerEnd > bytes.length || containerEnd < 20) {
    throw new Error('The WebP container is incomplete or damaged.');
  }

  let offset = 12;
  let hasImagePayload = false;
  let animated = false;
  let width = 0;
  let height = 0;
  while (offset < containerEnd) {
    if (offset + 8 > containerEnd) throw new Error('The WebP container has an incomplete chunk.');
    const type = ascii(bytes, offset, 4);
    const size = readU32LE(bytes, offset + 4);
    const payloadStart = offset + 8;
    if (size > containerEnd - payloadStart) throw new Error('The WebP container has a chunk outside its declared bounds.');
    const payload = bytes.subarray(payloadStart, payloadStart + size);
    if (type === 'ANIM' || type === 'ANMF') animated = true;
    if (type === 'VP8X') {
      if (size < 10) throw new Error('The WebP extended header is incomplete.');
      animated ||= (payload[0] & 0x02) !== 0;
      width = 1 + readU24(payload, 4);
      height = 1 + readU24(payload, 7);
    }
    if (type === 'VP8 ') {
      if (size < 10 || payload[3] !== 0x9d || payload[4] !== 0x01 || payload[5] !== 0x2a) throw new Error('The WebP image payload is malformed.');
      width ||= (payload[6] | (payload[7] << 8)) & 0x3fff;
      height ||= (payload[8] | (payload[9] << 8)) & 0x3fff;
      hasImagePayload = true;
    }
    if (type === 'VP8L') {
      if (size < 5 || payload[0] !== 0x2f) throw new Error('The WebP lossless payload is malformed.');
      width ||= 1 + ((payload[1] | (payload[2] << 8)) & 0x3fff);
      height ||= 1 + (((payload[2] >> 6) | (payload[3] << 2) | ((payload[4] & 0x3f) << 10)) & 0x3fff);
      hasImagePayload = true;
    }
    offset = payloadStart + size + (size % 2);
  }
  if (offset !== containerEnd) throw new Error('The WebP container has invalid chunk padding.');
  if (animated) throw new Error('Animated WebP is not supported. Choose a static WebP image.');
  if (!hasImagePayload || !width || !height) throw new Error('No supported static WebP image was found in this file.');
  if (width * height > MAX_PIXELS) throw new Error(`The decoded image is larger than ${MAX_PIXELS / 1000000} megapixels.`);
  return { mime: 'image/webp', width, height };
}

function detectMime(bytes) {
  if (isJpeg(bytes)) return 'image/jpeg';
  if (isPng(bytes)) {
    if (findPngChunk(bytes, 'acTL')) throw new Error('Animated PNG is not supported. Choose a static PNG image.');
    if (bytes.length < 24) throw new Error('The PNG header is incomplete.');
    const width = readU32BE(bytes, 16);
    const height = readU32BE(bytes, 20);
    if (!width || !height) throw new Error('The PNG has no usable dimensions.');
    return { mime: 'image/png', width, height };
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') return validateWebp(bytes);
  throw new Error('Only JPG, PNG, and static WebP images are supported. The file contents were checked.');
}

async function inspectFile(file) {
  if (!file || file.size === 0) throw new Error('The image is empty.');
  if (file.size > MAX_FILE_BYTES) throw new Error(`The image is larger than ${formatBytes(MAX_FILE_BYTES)}.`);
  const bytes = new Uint8Array(await file.arrayBuffer());
  const detected = detectMime(bytes);
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error('The browser could not decode this image.'));
    });
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height) throw new Error('The image has no usable decoded dimensions.');
    if (width * height > MAX_PIXELS) throw new Error(`The decoded image is larger than ${MAX_PIXELS / 1000000} megapixels.`);
    return { ...detected, width, height, url };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function describeOcrStatus(status) {
  const value = String(status || '').toLowerCase();
  if (value.includes('loading tesseract core')) return 'Loading the local OCR engine…';
  if (value.includes('loading language')) return 'Loading the selected language data…';
  if (value.includes('initializing')) return 'Preparing the local OCR worker…';
  if (value.includes('recogniz')) return 'Recognizing printed text…';
  if (value.includes('loading')) return 'Loading local OCR resources…';
  return 'Working locally…';
}

async function recognizeText() {
  if (!state.file || state.working) return;
  const runId = ++state.runId;
  const file = state.file;
  state.working = true;
  state.resultText = '';
  setStatus('Loading local OCR resources…');
  render();

  let worker = null;
  let rejectWorkerStart;
  const workerStartFailure = new Promise((_, reject) => { rejectWorkerStart = reject; });
  workerStartFailure.catch(() => {});
  let startTimer;
  try {
    const module = await import('tesseract.js');
    if (runId !== state.runId || file !== state.file) return;
    const createWorker = module.createWorker || module.default?.createWorker;
    if (typeof createWorker !== 'function') throw new Error('The local OCR engine could not be loaded.');
    installWorkerTracker();
    const workerPromise = createWorker(state.language, 1, {
        ...OCR_ASSETS,
        cachePath: 'kivoza-ocr-v7',
        cacheMethod: 'write',
        gzip: true,
        workerBlobURL: false,
        errorHandler: (message) => rejectWorkerStart(new Error(String(message || 'The local OCR worker failed to load.'))),
        logger: (message) => {
          if (runId === state.runId) setStatus(describeOcrStatus(message?.status));
        },
      });
    const workerTimeout = new Promise((_, reject) => {
      startTimer = setTimeout(() => reject(new Error('The local OCR resources took too long to load. Check the connection and try again.')), WORKER_START_TIMEOUT_MS);
    });
    try {
      worker = await Promise.race([workerPromise, workerStartFailure, workerTimeout]);
    } finally {
      clearTimeout(startTimer);
    }
    if (runId !== state.runId || file !== state.file) {
      await worker.terminate();
      return;
    }
    state.worker = worker;
    setStatus('Recognizing printed text…');
    const result = await worker.recognize(file);
    if (runId !== state.runId || file !== state.file) return;
    const text = String(result?.data?.text || '').trimEnd();
    if (!text.trim()) throw new Error('No printed text was recognized. Try a sharper, better-lit image.');
    state.resultText = text;
    setStatus('Recognition finished. Review and edit the text below.', 'success');
  } catch (error) {
    if (!worker) terminateTrackedWorkers();
    if (runId === state.runId) setStatus(error?.message || 'Recognition failed. Try again.', 'error');
  } finally {
    if (state.worker === worker) state.worker = null;
    if (worker) await worker.terminate().catch(() => {});
    if (runId === state.runId) {
      state.working = false;
      render();
    }
  }
}

async function selectFile(file) {
  if (!file) return;
  cancelCurrentRun();
  clearSelectedImage();
  setStatus('Checking image contents…');
  render();
  try {
    const inspected = await inspectFile(file);
    state.file = file;
    state.mime = inspected.mime;
    state.width = inspected.width;
    state.height = inspected.height;
    state.previewUrl = inspected.url;
    setStatus('Image ready. Choose a language and recognize the printed text.');
  } catch (error) {
    setStatus(error?.message || 'The image could not be read.', 'error');
  }
  render();
}

function clearTool() {
  cancelCurrentRun();
  clearSelectedImage();
  setStatus('Choose one image to begin.');
  render();
}

async function copyResult() {
  if (!state.resultText) return;
  if (!navigator.clipboard?.writeText) {
    elements.result.focus();
    elements.result.select();
    setStatus('Clipboard access needs HTTPS. The text is selected; press Ctrl+C or use your device copy command.', 'error');
    return;
  }
  try {
    await navigator.clipboard.writeText(state.resultText);
    setStatus('Text copied to the clipboard.', 'success');
  } catch {
    elements.result.focus();
    elements.result.select();
    setStatus('Clipboard access was blocked. The text is selected; copy it manually.', 'error');
  }
}

function downloadResult() {
  if (!state.resultText) return;
  const url = URL.createObjectURL(new Blob([state.resultText], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  const base = state.file?.name.replace(/\.[^.]+$/, '') || 'kivoza-ocr';
  link.download = `${base}.txt`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  setStatus('TXT download prepared. The recognized text stays editable in this tab.', 'success');
}

function handleDrop(event) {
  event.preventDefault();
  elements.dropZone.classList.remove('is-dragging');
  if (!state.working) selectFile(event.dataTransfer.files?.[0]);
}

elements.fileInput.addEventListener('change', () => {
  const file = elements.fileInput.files?.[0];
  elements.fileInput.value = '';
  selectFile(file);
});
elements.dropZone.addEventListener('click', (event) => {
  if (event.target !== elements.fileInput && !state.working) elements.fileInput.click();
});
elements.dropZone.addEventListener('keydown', (event) => {
  if ((event.key === 'Enter' || event.key === ' ') && !state.working) {
    event.preventDefault();
    elements.fileInput.click();
  }
});
elements.dropZone.addEventListener('dragover', (event) => {
  if (state.working) return;
  event.preventDefault();
  elements.dropZone.classList.add('is-dragging');
});
elements.dropZone.addEventListener('dragleave', () => elements.dropZone.classList.remove('is-dragging'));
elements.dropZone.addEventListener('drop', handleDrop);
document.addEventListener('paste', (event) => {
  if (state.working) return;
  const item = [...(event.clipboardData?.items || [])].find((entry) => entry.kind === 'file' && entry.type.startsWith('image/'));
  const file = item?.getAsFile();
  if (file) {
    event.preventDefault();
    selectFile(file);
  }
});
elements.language.addEventListener('change', () => {
  state.language = elements.language.value;
  if (state.resultText) {
    state.resultText = '';
    setStatus('Language changed. Recognize the image again.', '');
  }
  render();
});
elements.recognize.addEventListener('click', recognizeText);
elements.retry.addEventListener('click', recognizeText);
elements.clear.addEventListener('click', clearTool);
elements.removeImage.addEventListener('click', clearTool);
elements.result.addEventListener('input', () => {
  state.resultText = elements.result.value;
  render();
});
elements.copy.addEventListener('click', copyResult);
elements.download.addEventListener('click', downloadResult);
window.addEventListener('beforeunload', () => {
  revokePreview();
  stopWorker();
});

document.querySelectorAll('[data-year]').forEach((node) => {
  node.textContent = String(new Date().getFullYear());
});
setStatus('Choose one image to begin.');
render();
